import { useEffect, useMemo, useState } from 'react';
import { Box, Group, Progress, Stack, Text, Tooltip } from '@mantine/core';
import { AdditionalMealPreferences, MealPlanGenerationProgress, PlannedPenciledMeal } from '../../types/mealPlan';
import {
  OPENING_LINE,
  PlanHousehold,
  WAITING_LINE_SECONDS,
  hashSeed,
  linesFor,
  shuffledLines,
} from '../../utils/waitingLines';
import styles from './PlanGenerationProgress.module.css';

// The waiting copy is shared with the substitution overlay; existing callers
// keep importing it from here.
export { linesFor };
export type { PlanHousehold };

type Props = {
  progress?: MealPlanGenerationProgress;
  /**
   * When the plan was created, as an ISO string. Generation starts within a
   * fraction of a second of this, so it stands in for "how long has this
   * person been waiting" without a separate field.
   */
  startedAt?: string;
  /**
   * Day names the plan covers, in order. Labels the slots so the row reads as
   * the week filling in, and stands in for the total before the first progress
   * write lands.
   */
  expectedDays?: string[];
  /**
   * 'banner' is the dashboard notice at the top of the page, the one surface
   * with room to name the meals. 'tiles' sits where the finished plan's
   * thumbnails go and fills those slots in. 'compact' is a single line for
   * narrow card layouts.
   */
  variant: 'banner' | 'tiles' | 'compact';
  /**
   * How many slots the row has room for. The finished card shows seven
   * thumbnails on desktop and three on mobile, so the placeholders match
   * whichever one they stand in for. When the plan has more meals than slots,
   * the last slot carries a "+N" the way the finished card's does.
   */
  maxTiles?: number;
  /** The finished cards differ here too: sm on desktop, md on mobile. */
  tileRadius?: 'sm' | 'md';
  /**
   * Nights claimed by the user's own penciled-in meals. Generation skips
   * them, so they're absent from the progress totals — but the finished card
   * shows a thumbnail for every day, so the tiles show them too: filled from
   * the first frame, since they were decided at planning time.
   */
  penciledMeals?: PlannedPenciledMeal[];
  /**
   * Household this plan was built for, from its profile snapshot. Picks which
   * waiting lines make sense: the kid jokes need kids, and the "nobody has to
   * vote" ones need somebody to vote.
   */
  household?: PlanHousehold;
  /** Overrides the stage line, e.g. when several plans are generating at once. */
  label?: string;
  /**
   * The plan's breakfast/lunch configuration, so the "slots" stage (rotation
   * builds after the dinners land) can say which meals it's adding.
   */
  additionalMeals?: AdditionalMealPreferences;
};

const MAX_TILES = 7;

/**
 * When to stop claiming this is normal.
 *
 * Measured over 100 completed plans: median 113s, p90 185s. Three minutes sits
 * at roughly the p90, so the usual plan never trips this and a genuinely slow
 * one says so instead of leaving the estimate looking like a lie.
 */
const SLOW_AFTER_SECONDS = 180;

/** How often the waiting copy re-checks the clock. */
const CLOCK_INTERVAL_MS = 10_000;

/**
 * Where the bar sits, by stage.
 *
 * Writing the meals is the bulk of the wait but not all of it: photos,
 * nutrition, swap options and the final checks take a fifth of it. So the
 * meals fill the middle of the bar rather than all of it, and the closing
 * stages carry it the rest of the way instead of parking at 100%.
 */
const PLANNING_FLOOR = 8;
const WRITING_FLOOR = 30;
const WRITING_CEILING = 80;

/**
 * Planning advances on how much reasoning has actually been produced.
 *
 * Before the first recipe there is no countable work, but the plan is being
 * thought through and that reasoning streams out as it happens. Its volume is
 * a real signal: the bar creeps while the plan is genuinely being worked on
 * and stalls if the work stalls, which a clock-driven bar cannot do.
 *
 * Asymptotic rather than linear because the total is unknowable ahead of time,
 * so it decelerates toward WRITING_FLOOR without ever reaching it. The bar
 * arriving there means real recipes have arrived, not that planning ran long.
 *
 * Calibrated on the distribution, not on one run. An earlier value of 8000
 * came from a single watched generation that produced 19.6k characters, which
 * turned out to sit above the 90th percentile: measured across the last 60
 * production generations, reasoning runs p10 3.9k, median 6.4k, p90 11.2k.
 * At 8000 the median plan only reached 20 before the handover, so it jumped
 * ten points the moment writing began. This targets the median instead.
 *
 * Reasoning volume does not track plan size, which is worth knowing before
 * anyone tries to scale this by meal count: measured by recipes requested,
 * the medians run 5.4k at one recipe and 4.1k at seven. How hard the week is
 * to think through has little to do with how many nights are in it.
 *
 * The cost is at the top of the range. A plan that reasons past ~8k spends
 * its last stretch close to the floor with little left to travel, which is
 * the lesser problem: by then the handover is moments away, where a jump at
 * the handover is the thing being felt.
 */
const THINKING_SCALE_CHARS = 2600;

const planningPercent = (thinkingChars: number): number =>
  PLANNING_FLOOR +
  (WRITING_FLOOR - PLANNING_FLOOR) *
    (1 - Math.exp(-thinkingChars / THINKING_SCALE_CHARS));

// On plans with breakfast/lunch, the slot builds after the dinners are a real
// stretch of the wait (rotation picks, serving math), so the closing stages
// compress to leave the slots stage a range to travel, paced by its reported
// unit completions. Dinner-only plans keep the original closing numbers.
const SLOTS_FLOOR = 89;
const SLOTS_CEILING = 98;

const barPercent = (
  progress: MealPlanGenerationProgress | undefined,
  mealCount: number,
  totalMeals: number,
  hasSlots: boolean
): number => {
  const mealShare = totalMeals > 0 ? mealCount / totalMeals : 0;
  const writing =
    WRITING_FLOOR + mealShare * (WRITING_CEILING - WRITING_FLOOR);

  switch (progress?.stage) {
    case 'parsing':
      return 3;
    case 'retrieving':
      return 6;
    case 'planning':
      // Plans generated before reasoning volume was reported sit at the floor.
      return planningPercent(progress.thinkingChars ?? 0);
    case 'writing':
      return writing;
    case 'enriching':
      return hasSlots ? 82 : 85;
    case 'alternatives':
      return hasSlots ? 86 : 92;
    case 'finishing':
      return hasSlots ? SLOTS_FLOOR : 97;
    case 'slots': {
      const unitShare = progress.slotsTotal
        ? Math.min((progress.slotsDone ?? 0) / progress.slotsTotal, 1)
        : 0;
      return SLOTS_FLOOR + unitShare * (SLOTS_CEILING - SLOTS_FLOOR);
    }
    default:
      // Plans started before stages were reported.
      return mealShare * 100;
  }
};

// Only reported on plans with breakfast/lunch configured; the fallback covers
// a progress doc arriving ahead of the plan's preferences.
const slotsStageLabel = (additionalMeals?: AdditionalMealPreferences): string =>
  additionalMeals?.breakfast && additionalMeals?.lunch
    ? 'Adding breakfasts and lunches'
    : additionalMeals?.breakfast
      ? 'Adding breakfasts'
      : additionalMeals?.lunch
        ? 'Adding lunches'
        : 'Almost ready';

const stageLabel = (
  progress: MealPlanGenerationProgress | undefined,
  mealCount: number,
  totalMeals: number,
  additionalMeals?: AdditionalMealPreferences
): string => {
  // A restart is worth saying out loud, otherwise the meals emptying out reads
  // as progress being lost.
  if ((progress?.attempt ?? 1) > 1 && mealCount === 0) {
    return 'Giving your week another go';
  }

  // Written from the kitchen's side rather than the machine's: what a person
  // planning your week would say they were doing. Nothing here narrates how
  // the plan is produced.
  switch (progress?.stage) {
    case 'parsing':
      return 'Going over your notes';
    case 'retrieving':
      return 'Flipping through your cookbook';
    case 'planning':
      return 'Planning your week';
    case 'writing':
      return 'Filling in your week';
    case 'enriching':
      return 'Plating everything up';
    case 'alternatives':
      return 'Picking a few backups';
    case 'finishing':
    case 'slots':
      // The breakfast/lunch build runs after finishing, so on slot plans
      // "Almost ready" here would read out of sequence: the brief finishing
      // stage borrows the slots label instead. Dinner-only plans keep it.
      return slotsStageLabel(additionalMeals);
    default:
      // Plans started before stages were reported, and the brief window before
      // the first progress write lands.
      if (mealCount === 0) return 'Planning your week';
      return mealCount >= totalMeals && totalMeals > 0
        ? 'Almost ready'
        : 'Filling in your week';
  }
};

/**
 * Shows a plan taking shape while it is still being written.
 *
 * Meals are named as they are decided, which is the honest progress signal:
 * most of the wait is the plan being written one meal at a time. Names shown
 * here already match how the finished plan will display them, though a recipe
 * can still be swapped by the checks that run at the end, so this reads as
 * work in progress rather than a final lineup.
 */
export const PlanGenerationProgress = ({
  progress,
  startedAt,
  expectedDays,
  variant,
  household,
  label,
  maxTiles = MAX_TILES,
  tileRadius = 'sm',
  penciledMeals,
  additionalMeals,
}: Props) => {
  const meals = progress?.meals ?? [];
  const totalMeals = progress?.totalMeals || expectedDays?.length || 0;
  const status = label ?? stageLabel(progress, meals.length, totalMeals, additionalMeals);
  const hasSlots = !!(additionalMeals?.breakfast || additionalMeals?.lunch);
  const percent = barPercent(progress, meals.length, totalMeals, hasSlots);

  // Only while meals are still landing. Once the plan moves on to photos and
  // final checks, the last name is stale and the stage line says more.
  const stillWriting = progress?.stage
    ? progress.stage === 'writing'
    : meals.length > 0 && meals.length < totalMeals;
  const latestMeal = stillWriting ? meals[meals.length - 1] : undefined;

  // Nothing has been decided yet, so the wait itself is the only thing worth
  // saying anything about. Re-checked on a timer because this stretch can pass
  // without a single progress write to re-render on.
  const waiting = meals.length === 0;
  const [now, setNow] = useState(() => Date.now());
  // Deliberately keyed on whether a start time exists, not on its value. A
  // caller that recomputes the timestamp between renders would otherwise tear
  // the interval down and rebuild it before it ever fired, leaving the copy
  // frozen on its first line and the slow-run warning permanently unreachable.
  const hasStartedAt = Boolean(startedAt);
  useEffect(() => {
    if (!waiting || !hasStartedAt) return;
    const id = setInterval(() => setNow(Date.now()), CLOCK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [waiting, hasStartedAt]);

  const elapsedSeconds = startedAt
    ? (now - new Date(startedAt).getTime()) / 1000
    : 0;
  const runningLong = Boolean(startedAt) && elapsedSeconds > SLOW_AFTER_SECONDS;

  // Shuffled once per plan, not per render, so the line does not jump around
  // between renders that happen inside the same ten second slot.
  const lineOrder = useMemo(
    () => shuffledLines(linesFor(household), hashSeed(startedAt ?? 'plan')),
    [startedAt, household]
  );

  // Always populated, so the banner is the same height from the first frame to
  // the last. An empty or conditional line here makes the whole dashboard jump
  // when the first meal lands and again when writing ends.
  const waitingText = waiting
    ? runningLong
      ? // Stays plain. Someone four minutes into a wait is not in the mood to
        // be charmed, and a joke here would read as not taking it seriously.
        'Hang tight, this is taking longer than usual'
      : // The expectation lands first, then it cycles. Clamped at zero because
        // a start time in the future (a client clock behind the server's) would
        // otherwise index off the front and render nothing at all.
        (() => {
          const slot = Math.max(
            0,
            Math.floor(elapsedSeconds / WAITING_LINE_SECONDS)
          );
          if (slot === 0) return OPENING_LINE;
          return lineOrder[(slot - 1) % lineOrder.length];
        })()
    : hasSlots
      ? // Breakfasts and lunches still land after the dinner stages, so
        // claiming the whole week here would run ahead of the stage line.
        'Dinners are planned'
      : 'Your week is planned';

  // Keyed separately from the content so the entrance still replays on change
  // now that the meal case renders markup rather than a plain string.
  const detailKey = latestMeal ? `meal:${latestMeal.name}` : waitingText;
  const detailContent = latestMeal ? (
    <>
      Just added{' '}
      <Text component="span" fw={700} inherit>
        {latestMeal.name}
      </Text>
    </>
  ) : (
    waitingText
  );

  if (variant === 'tiles') {
    // One slot per planned day, filling in where the photos will land. The
    // slots carry the day rather than the meal: a recipe name does not fit an
    // 80px tile without truncating, so the names live in the banner and in a
    // tooltip here.
    type TileSlot = { day?: string; name?: string; filled: boolean };
    const penciled = penciledMeals ?? [];
    let slots: TileSlot[];
    if (penciled.length > 0 && expectedDays && expectedDays.length > 0) {
      // Penciled plans get a slot for every day of the week, in week order:
      // penciled nights are filled from the start (they were decided at
      // planning time and the finished card shows them), and generated meals
      // land on their own day rather than positionally.
      const mealByDay = new Map(meals.map((meal) => [meal.plannedDay, meal]));
      const penciledByDay = new Map(penciled.map((p) => [p.day, p.name]));
      slots = expectedDays.map((day) => {
        const penciledName = penciledByDay.get(day);
        const meal = mealByDay.get(day);
        return {
          day,
          name: penciledName ?? meal?.name,
          filled: Boolean(penciledName || meal),
        };
      });
    } else {
      // No penciled nights: one slot per expected meal, filled positionally
      // as meals arrive. Deliberately NOT per-day — with meal prep the plan
      // has fewer meals than days, and day slots would leave prep days
      // reading as forever-pending.
      slots = Array.from({ length: totalMeals || maxTiles }).map(
        (_, index) => ({
          day: meals[index]?.plannedDay ?? expectedDays?.[index],
          name: meals[index]?.name,
          filled: Boolean(meals[index]),
        })
      );
    }

    const tileCount = Math.min(slots.length, maxTiles);
    // A week does not fit in three slots, so the last one becomes a "+5" the
    // way the finished card's third thumbnail does. It counts the plan's
    // remainder rather than what has arrived, so the number means the same
    // thing before and after the photos land and does not move on its own.
    const overflowCount =
      slots.length > maxTiles ? slots.length - (maxTiles - 1) : 0;

    return (
      <Group gap="xs" wrap="nowrap">
        {slots.slice(0, tileCount).map((slot, index) => {
          const isOverflow = overflowCount > 0 && index === tileCount - 1;
          // The overflow slot stands for every slot past the ones on screen,
          // so it settles when the first of those fills rather than tracking
          // one meal.
          const hidden = isOverflow ? slots.slice(tileCount - 1) : [];
          const filled = isOverflow
            ? hidden.some((entry) => entry.filled)
            : slot.filled;

          const tile = (
            <Box
              className={`${styles.tile} ${
                tileRadius === 'md' ? styles.tileRadiusMd : ''
              } ${filled ? styles.tileFilled : styles.tilePending}`}
            >
              <Text size="xs" fw={filled ? 600 : 500} className={styles.tileDay}>
                {isOverflow
                  ? `+${overflowCount}`
                  : slot.day
                    ? slot.day.slice(0, 3)
                    : ''}
              </Text>
            </Box>
          );

          // Named meals get a tooltip; the overflow slot lists the ones it
          // stands for, as the finished card's does.
          const names = isOverflow
            ? hidden
                .map((entry) => entry.name)
                .filter(Boolean)
                .join(', ')
            : slot.name;

          if (!names) return <Box key={index}>{tile}</Box>;

          return (
            <Tooltip
              key={index}
              label={names}
              position="top"
              withArrow
              multiline
              maw={300}
              events={{ hover: true, focus: true, touch: true }}
            >
              {tile}
            </Tooltip>
          );
        })}
      </Group>
    );
  }

  const progressBar = (
    <Progress
      value={percent}
      size={variant === 'banner' ? 'sm' : 'xs'}
      radius="xl"
      // Matches the in-progress notice this sits inside, and keeps the brand
      // orange for the plan itself rather than the machinery around it.
      color="blue"
      // Not `animated`: that prop forces Mantine's barber-pole stripes and
      // there is no way to keep the motion without them. The bar carries its
      // own highlight sweep instead, which reads as working without the
      // candy-cane texture. It always animates, since this only renders while
      // a plan is in flight.
      classNames={{ section: styles.barFill }}
      transitionDuration={400}
    />
  );

  if (variant === 'compact') {
    return (
      <Stack gap={6}>
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Text size="sm" fw={500}>
            {status}
          </Text>
          {meals.length > 0 && totalMeals > 0 && (
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {meals.length}/{totalMeals}
            </Text>
          )}
        </Group>
        {progressBar}
      </Stack>
    );
  }

  return (
    <Stack gap={8}>
      <Group justify="space-between" gap="xs" wrap="nowrap" align="baseline">
        <Text size="sm" fw={600}>
          {status}
        </Text>
        {meals.length > 0 && totalMeals > 0 && (
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {meals.length} of {totalMeals} meals
          </Text>
        )}
      </Group>

      {progressBar}

      {/* The meal that just landed, rather than every meal so far. A growing
          list wraps partway through and pushes the page down, and the week
          strip on the card already carries the full set. Keyed on the text so
          each new meal re-runs the entrance. */}
      <Text
        key={detailKey}
        size="sm"
        c="dimmed"
        lineClamp={2}
        className={`${styles.mealName} ${styles.detailLine}`}
      >
        {detailContent}
      </Text>
    </Stack>
  );
};
